import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  AlertCircle,
  Brain,
  Heart,
  Users,
  Activity,
  Cloud,
  Zap,
  Loader2
} from 'lucide-react';
import AssessmentLayout from '../../components/assessment/AssessmentLayout';
import { useSupabaseQuery } from '../../hooks/useSupabase';

// Icon mapping helper
const iconMap: Record<string, React.ElementType> = {
  'Cloud': Cloud,
  'AlertCircle': AlertCircle,
  'Zap': Zap,
  'Heart': Heart,
  'Activity': Activity,
  'Brain': Brain,
  'Users': Users,
};

interface Category {
  id: string;
  name_no: string;
  description?: string;
  order_index: number;
  selected: boolean;
  icon: React.ElementType;
}

export default function AssessmentCategories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);

  // Fetch symptom groups from Supabase
  const { data: symptomGroups, loading: symptomGroupsLoading } = useSupabaseQuery<any[]>('symptom_groups', {
    filter: (q: any) => q.eq('status', 'published'),
    order: { column: 'order_index', ascending: true },
  });

  // Transform Supabase data to local state
  useEffect(() => {
    if (symptomGroups) {
      const defaultIcons = [Cloud, AlertCircle, Zap, Heart, Activity, Brain, Users];
      const transformed = symptomGroups.map((group: any, index: number) => ({
        id: group.id,
        name_no: group.name_no,
        description: group.description,
        order_index: group.order_index,
        selected: false,
        icon: defaultIcons[index % defaultIcons.length],
      }));
      setCategories(transformed);
    }
  }, [symptomGroups]);

  const handleCategoryClick = (id: string) => {
    setCategories(cats =>
      cats.map(cat =>
        cat.id === id ? { ...cat, selected: !cat.selected } : cat
      )
    );
  };

  const handleContinue = () => {
    const selectedCategories = categories.filter(c => c.selected);
    if (selectedCategories.length === 0) {
      alert('Vennligst velg minst én kategori');
      return;
    }

    // Store selected category IDs
    sessionStorage.setItem('selectedCategoryIds', JSON.stringify(selectedCategories.map(c => c.id)));
    navigate('/assessment/symptoms');
  };

  const selectedCount = categories.filter(c => c.selected).length;

  // Show loading state
  if (symptomGroupsLoading) {
    return (
      <AssessmentLayout
        title="Modulbasert tilnærming for betjenter"
        subtitle="Laster symptomgrupper..."
        backTo="/assessment/guidance"
        currentStep={3}
      >
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
        </div>
      </AssessmentLayout>
    );
  }

  if (!symptomGroups || symptomGroups.length === 0) {
    return (
      <AssessmentLayout
        title="Modulbasert tilnærming for betjenter"
        subtitle="Kunne ikke laste data"
        backTo="/assessment/guidance"
        currentStep={3}
      >
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400 mb-4">Kunne ikke laste symptomgrupper</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Prøv igjen
          </button>
        </div>
      </AssessmentLayout>
    );
  }

  return (
    <AssessmentLayout
      title="Modulbasert tilnærming for betjenter"
      subtitle="Velg hovedgrupper som passer"
      backTo="/assessment/guidance"
      currentStep={3}
    >
      {/* Category Cards Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {categories.map((category, index) => {
          const Icon = category.icon;
          return (
            <button
              key={category.id}
              onClick={() => handleCategoryClick(category.id)}
              className={`
                bg-white dark:bg-gray-800 rounded-lg p-6 text-left transition-all
                border-2 hover:shadow-md
                ${category.selected
                  ? 'border-blue-600 dark:border-blue-500 shadow-sm'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }
              `}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                    <Icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">{index + 1}</span>
                </div>
                {category.selected && (
                  <div className="bg-blue-600 dark:bg-blue-500 rounded-full p-1">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                )}
              </div>

              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{category.name_no}</h3>
              {category.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400">{category.description}</p>
              )}
            </button>
          );
        })}
      </div>

      {/* Footer with counter and continue button */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              {selectedCount > 0 ? (
                <>
                  <span className="text-blue-600 dark:text-blue-400">{selectedCount}</span>
                  <span className="text-gray-600 dark:text-gray-400 ml-2">av {categories.length} kategorier valgt</span>
                </>
              ) : (
                <span className="text-gray-500 dark:text-gray-500">Ingen kategorier valgt</span>
              )}
            </p>
          </div>

          <button
            onClick={handleContinue}
            disabled={selectedCount === 0}
            className={`
              flex items-center gap-2 px-6 py-3 rounded-lg font-semibold
              transition-colors
              ${selectedCount > 0
                ? 'bg-green-600 dark:bg-green-500 text-white hover:bg-green-700 dark:hover:bg-green-600'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }
            `}
          >
            Gå videre til symptomer
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </AssessmentLayout>
  );
}
